import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link2, Loader, Trash2, X } from 'lucide-react';
import toast from 'react-hot-toast';
import axiosInstance from '../../api/axios';
import { API_ENDPOINTS } from '../../api/endpoints';
import { fetchFlowchartVersions } from '../../request/flowchart/flowchart.api';
import {
  deleteFlowchartSubprocessLink,
  fetchFlowchartSubprocessLinks,
  formatSubprocessNodeLabel,
} from '../../request/flowchart/subprocessLinks.api';
import { combinedVersionLabel } from './pickCombinedFlowchartVersion';

function processCode(process) {
  return String(process?.process_id || process?.id || '').trim();
}

function processUuid(process) {
  return String(process?.id || process?.process_uuid || '').trim();
}

function processName(process) {
  return String(process?.name || processCode(process) || 'Process').trim();
}

function flowchartNodesFromPayload(flowchartData) {
  const nodes = Array.isArray(flowchartData?.nodes) ? flowchartData.nodes : [];
  return nodes
    .filter((node) => {
      if (!node?.id) return false;
      const type = String(
        node?.nodeType || node?.type || node?.data?.nodeType || node?.data?.type || ''
      ).toLowerCase();
      return type !== 'start' && type !== 'end' && type !== 'swimlane';
    })
    .map((node) => ({
      id: String(node.id),
      nodeDescription:
        node.nodeDescription || node.node_description || node.data?.nodeDescription || '',
      nodeType: node.nodeType || node.type || node.data?.nodeType || '',
      actor: node.actorRef?.name || node.actor || node.data?.actorRef?.name || node.data?.actor || '',
    }));
}

async function fetchVersionFlowchart(cycleId, processId, versionNumber) {
  const response = await axiosInstance.get(API_ENDPOINTS.getFlowchart(cycleId, processId), {
    params: { versionNumber },
  });
  if (!response?.data?.success || !response.data?.flowchartData) {
    throw new Error(response?.data?.error || 'Failed to load flowchart');
  }
  return {
    flowchartVersionId: response.data.flowchartVersionId || null,
    versionNumber: response.data.versionNumber ?? versionNumber,
    nodes: flowchartNodesFromPayload(response.data.flowchartData),
  };
}

function formatSourceNodeLabel(node, link) {
  if (node) {
    const description = String(node.nodeDescription || '').trim() || 'Untitled step';
    const actor = String(node.actor || '').trim();
    return actor ? `${description} · ${actor}` : description;
  }
  return formatSubprocessNodeLabel({
    nodeDescription: link?.source_node_description,
    nodeType: link?.source_node_type,
  }) || link?.source_node_id || 'Unknown node';
}

export default function EndToEndConnectorLinksModal({
  isOpen,
  cycleId,
  sourceProcess,
  targetProcess,
  onClose,
  onChanged,
  onConnectMore,
}) {
  const [versionGroups, setVersionGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deletingLinkId, setDeletingLinkId] = useState('');

  const sourceCode = processCode(sourceProcess);
  const targetCode = processCode(targetProcess);
  const targetUuid = processUuid(targetProcess);

  const totalLinks = useMemo(
    () =>
      versionGroups.reduce(
        (count, group) => count + (Array.isArray(group.links) ? group.links.length : 0),
        0
      ),
    [versionGroups]
  );

  const loadAllVersionLinks = useCallback(async () => {
    if (!cycleId || !sourceCode || !targetUuid) {
      setVersionGroups([]);
      return;
    }
    setLoading(true);
    try {
      const versions = await fetchFlowchartVersions(cycleId, sourceCode);
      const groups = await Promise.all(
        (Array.isArray(versions) ? versions : []).map(async (version) => {
          const versionNumber = version?.value;
          const versionLabel = combinedVersionLabel(version) || version?.label || 'Version';
          const flowchartVersionId = String(version?.flowchartVersionId || '').trim();

          if (!flowchartVersionId || versionNumber == null) {
            return {
              versionNumber,
              versionLabel,
              flowchartVersionId,
              nodes: [],
              links: [],
            };
          }

          const links = await fetchFlowchartSubprocessLinks(flowchartVersionId);
          const linksToTarget = (Array.isArray(links) ? links : []).filter(
            (link) => String(link?.target_process_id || '') === targetUuid
          );

          let nodes = [];
          if (linksToTarget.length > 0) {
            const chart = await fetchVersionFlowchart(cycleId, sourceCode, versionNumber);
            nodes = chart.nodes || [];
          }

          return {
            versionNumber,
            versionLabel,
            flowchartVersionId,
            nodes,
            links: linksToTarget,
          };
        })
      );

      setVersionGroups(
        groups.filter((group) => Array.isArray(group.links) && group.links.length > 0)
      );
    } catch (err) {
      setVersionGroups([]);
      toast.error(err?.message || 'Failed to load subprocess links');
    } finally {
      setLoading(false);
    }
  }, [cycleId, sourceCode, targetUuid]);

  useEffect(() => {
    if (!isOpen) {
      setVersionGroups([]);
      setDeletingLinkId('');
      return;
    }
    loadAllVersionLinks();
  }, [isOpen, loadAllVersionLinks]);

  const handleDelete = useCallback(
    async (linkId) => {
      if (!linkId) return;
      setDeletingLinkId(linkId);
      try {
        await deleteFlowchartSubprocessLink(linkId);
        toast.success('Subprocess link removed');
        await loadAllVersionLinks();
        onChanged?.();
      } catch (err) {
        toast.error(err?.message || 'Failed to remove subprocess link');
      } finally {
        setDeletingLinkId('');
      }
    },
    [loadAllVersionLinks, onChanged]
  );

  if (!isOpen || !sourceProcess || !targetProcess) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4 backdrop-blur-[2px]">
      <div className="w-full max-w-xl overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-[0_24px_60px_rgba(15,23,42,0.18)]">
        <div className="flex items-start justify-between gap-3 border-b border-slate-100 px-5 py-4">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
              Process connection links
            </p>
            <h2 className="mt-1 text-lg font-semibold text-slate-900">
              {processName(sourceProcess)}
              <span className="mx-2 text-slate-300">→</span>
              {processName(targetProcess)}
            </h2>
            <p className="mt-1 text-xs text-slate-500">
              {sourceCode} to {targetCode} · All source versions with links to this process
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-700"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="max-h-[70vh] overflow-y-auto px-5 py-4">
          {loading ? (
            <div className="flex items-center justify-center py-10 text-sm text-slate-500">
              <Loader className="mr-2 h-4 w-4 animate-spin" />
              Loading links across versions...
            </div>
          ) : totalLinks === 0 ? (
            <p className="py-8 text-center text-sm text-slate-500">
              No subprocess links from {sourceCode} to {targetCode}.
            </p>
          ) : (
            <div className="space-y-5">
              {versionGroups.map((group) => (
                <section key={String(group.flowchartVersionId || group.versionNumber)}>
                  <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                    {group.versionLabel}
                  </p>
                  <div className="space-y-2">
                    {group.links.map((link) => {
                      const fromNode = (group.nodes || []).find(
                        (node) => String(node.id) === String(link.source_node_id)
                      );
                      return (
                        <div
                          key={link.id}
                          className="flex items-start justify-between gap-2 rounded-lg border border-slate-200 bg-slate-50/80 p-3"
                        >
                          <div className="min-w-0">
                            <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                              From
                            </p>
                            <p className="text-sm font-medium text-slate-900">
                              {formatSourceNodeLabel(fromNode, link)}
                            </p>
                            <p className="mt-2 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                              To
                            </p>
                            <p className="text-sm font-medium text-slate-900">
                              {link.target_process_display_id || targetCode}
                            </p>
                            <p className="mt-1 text-xs text-slate-600">
                              {formatSubprocessNodeLabel({
                                nodeDescription: link.target_node_description,
                                nodeType: link.target_node_type,
                              })}
                            </p>
                          </div>
                          <button
                            type="button"
                            onClick={() => handleDelete(link.id)}
                            disabled={deletingLinkId === link.id}
                            className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-red-500 hover:bg-red-50"
                            aria-label="Remove subprocess link"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </section>
              ))}
            </div>
          )}
        </div>

        <div className="border-t border-slate-100 px-5 py-3">
          <button
            type="button"
            onClick={() => onConnectMore?.({ sourceProcess, targetProcess })}
            className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-blue-600 px-3 py-2.5 text-sm font-medium text-white hover:bg-blue-700"
          >
            <Link2 className="h-4 w-4" />
            Connect more
          </button>
        </div>
      </div>
    </div>
  );
}
